import React from "react";
import { SymbolAvatar } from "@/components/common";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { Info } from "lucide-react";
import type { VybeOrderInternal } from "@/vybe-trader-client/trade-history/types";
import {
  formatOrderType,
  formatOrderState,
  VybeOrderType,
  VybePlacedType,
  orderTypeBuySellClass,
} from "@/vybe-trader-client/trade-history/types";
import { formatHistoryLocalDateTime } from "@/components/history-view/historyDateTimeFormat";
import { DirectionStatusCell, deriveStatus } from "@/components/common";

interface OrderItemProps {
  order: VybeOrderInternal;
  onClick?: () => void;
}

export default function OrderItem({ order, onClick }: OrderItemProps) {
  const orderType = formatOrderType(order.type);
  const orderState = formatOrderState(order.state);
  const direction =
    orderTypeBuySellClass(order.type) === "text-red-400" ? "sell" : "buy";
  const status = deriveStatus(order.state);

  // Determine price display
  const getPriceDisplay = () => {
    // Stop Loss or Take Profit placed types always show price
    if (
      order.placedType === VybePlacedType.OnSL ||
      order.placedType === VybePlacedType.OnTP
    ) {
      return `at ${order.price.toFixed(order.digits)}`;
    }

    // Other automated executions that should show price
    if (
      order.placedType === VybePlacedType.OnStopOut ||
      order.placedType === VybePlacedType.OnRollover ||
      order.placedType === VybePlacedType.OnVmargin ||
      order.placedType === VybePlacedType.OnSplit
    ) {
      return `at ${order.price.toFixed(order.digits)}`;
    }

    // CloseBy orders show price
    if (order.type === VybeOrderType.CloseBy) {
      return `at ${order.price.toFixed(order.digits)}`;
    }

    // Limit and Stop orders show openPrice
    if (order.isLimitOrder || order.isStopOrder || order.isStopLimitOrder) {
      return `at ${order.openPrice.toFixed(order.digits)}`;
    }

    // Market orders (Buy/Sell) that are trade orders show "at market"
    if (
      order.isTradeOrder &&
      (order.type === VybeOrderType.Buy || order.type === VybeOrderType.Sell)
    ) {
      return "at market";
    }

    // Default to price if available, otherwise market
    if (order.price > 0) {
      return `at ${order.price.toFixed(order.digits)}`;
    }

    return "at market";
  };

  // Format lots display - show requested/filled
  const getLotsDisplay = () => {
    // For cancelled orders, show requested / 0.00
    if (order.state === 2) {
      // VybeOrderState.Cancelled
      return `${order.lots} / 0.00`;
    }

    // For pending orders (not filled yet), show requested / 0.00
    if (order.state === 1 || order.state === 0) {
      // Placed or Started
      return `${order.lots} / 0.00`;
    }

    // For filled orders, show requested / filled
    if (order.state === 4) {
      // VybeOrderState.Filled
      return `${order.lots} / ${order.lots}`;
    }

    // For partial fills (state 3), we would need actual filled volume
    // Since we don't have it in VybeOrderInternal, we show requested / requested
    if (order.state === 3) {
      // VybeOrderState.Partial
      // In real scenario, we'd need filledLots field
      return `${order.lots} / ${order.lots} (partial)`;
    }

    // Default
    return order.lots.toString();
  };

  const getStateColor = () => {
    switch (order.state) {
      case 4:
        return "text-blue-400";
      case 2:
        return "text-red-400";
      default:
        return "text-yellow-400";
    }
  };

  const displayTimeMs =
    order.executionTime > 0
      ? order.executionTime * 1000
      : order.openTimeMs > 0
        ? order.openTimeMs
        : null;
  const orderWhen =
    displayTimeMs !== null ? formatHistoryLocalDateTime(displayTimeMs) : "—";

  const row = (
    <div
      className="flex items-center gap-3 py-2 border-b border-border cursor-pointer px-1"
      onClick={onClick}
    >
      <SymbolAvatar name={order.symbol} />
      <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <DirectionStatusCell status={status} direction={direction} />
          <span className="text-foreground font-medium">{order.symbol}</span>
          <span
            className={`text-sm ${orderTypeBuySellClass(order.type)}`}
          >
            {orderType}
          </span>
        </div>
        <span className={getStateColor()}>{orderState}</span>
      </div>
      <div className="flex items-center justify-between gap-2 text-muted-foreground text-xs">
        <span className="min-w-0 truncate">
          {getLotsDisplay()} {getPriceDisplay()}
        </span>
<span className="shrink-0 font-mono opacity-70">#{order.ticketNumber ?? order.ticket}</span>
                <span className="shrink-0 text-right tabular-nums whitespace-nowrap">
          {orderWhen}
        </span>
      </div>
      </div>
    </div>
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={() => onClick?.()}>
          <Info className="mr-2 h-4 w-4" />
          Show details
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
